import { AddBoxOutlined, Circle } from '@mui/icons-material'
import { Box, Card, CircularProgress, Grid, Icon, Stack, Tooltip, Typography } from '@mui/material'
import LinearProgress from '@mui/material/LinearProgress'
import { OTHER, REFRESH } from 'common/components/action/ActionConstant'
import useActions from 'common/hooks/useActions'
import useFetch from 'common/hooks/useFetch'
import useTitles from 'common/hooks/useTitles'
import ApplicationConf from 'conf/ApplicationConf'
import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { orderBy, slice } from 'lodash'
import { Draggable } from 'common/components/dragndrop/Draggable'
import { DndContext } from '@dnd-kit/core'
import { Droppable } from 'common/components/dragndrop/Droppable'
import { fetchUsers } from 'admin/users/UsersSlice'
import { STATUS_TICKETS, STATUS_TICKETS_LABEL } from 'common/constants/Constants'
import { PASTEL_COLOR } from 'common/utils/ColorUtil'
import { mainBlue } from 'app/theme'
import ContextMenu from 'common/components/ContextMenu'
import { useParams } from 'react-router'
import IssuesAction from 'common/actions/IssuesAction'
import { DialogAddNoteTicket } from 'recette/DialogTicket'
import moment from 'moment'
import { getIconPriority, truncateString } from 'common/utils/StringUtil'
import DialogEditAvancement from 'dev/DialogEditAvancement'
import { STATUS } from 'common/constants/storeConstants'
import { LOT_GROUP, tintColor, withDevLots } from './DevLotsUtils'
import { Pill, progressSx } from './LotHeader'

const ID_TICKETS_TO_COME = 1
const ID_TICKETS_TO_DO = 2
const ID_TICKETS_ONGOING = 3
const ID_TICKETS_DONE = 4

const LOT_MAX_LENGTH = 40

// Carte d'un ticket (style du Suivi des lots) : bandeau gauche à la couleur du statut, titre, puis projet, lot,
// affecté (vue « all ») et avancement.
const TicketCard = ({
    ticket = {},
    id,
    activeTicket,
    loading,
    onEditAvancement = () => {},
    showLogin,
}) => {
    const { icon, color } = getIconPriority(ticket.priority)
    const statusColor = STATUS_TICKETS.find(s => s.value === ticket.status)?.color ?? '#cfd6e4'
    const avancement = parseInt(ticket.avancement) || 0
    return (
        <ContextMenu
            key={ticket.id}
            actions={[{
                label: 'Consulter le ticket',
                onClick: () => window.open(`https://support.aquasys.fr/view.php?id=${ticket.id}`, '_blank'),
                icon: 'launch',
            }, {
                label: 'Modifier l\'avancement',
                onClick: () => onEditAvancement(ticket),
                icon: 'edit',
            }]}
        >
            <Draggable id={`${ticket.id}_${id}`}>
                <Box
                    sx={{
                        display: 'flex',
                        backgroundColor: 'white',
                        border: '1px solid #e6eaf2',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        '&:hover': { boxShadow: '0 1px 4px rgba(28,35,64,0.12)' },
                    }}
                >
                    <Tooltip title={ticket.status ?? ''} placement='left'>
                        <Box sx={{ width: 4, flexShrink: 0, backgroundColor: statusColor }} />
                    </Tooltip>
                    {activeTicket?.id === ticket.id && loading ? (
                        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={22} /></Box>
                    ) : (
                        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0, p: '8px 10px' }}>
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
                                <Tooltip title={`Priorité : ${ticket.priority}`}>
                                    <Icon sx={{ fontSize: 15, mt: '2px', color }}>{icon}</Icon>
                                </Tooltip>
                                <Typography sx={{ fontSize: 13.5, color: '#1c2340', lineHeight: 1.35 }}>
                                    <Box component='span' sx={{ color: '#6b7893', mr: 0.5 }}>{`#${ticket.id}`}</Box>
                                    {ticket.summary}
                                </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                                <Typography sx={{ fontSize: 12, color: '#6b7893' }}>{ticket.project}</Typography>
                                {ticket.lot && (
                                    <Tooltip title={ticket.lot.length > LOT_MAX_LENGTH ? ticket.lot : ''}>
                                        <Box><Pill>{truncateString(ticket.lot, LOT_MAX_LENGTH)}</Pill></Box>
                                    </Tooltip>
                                )}
                                {showLogin && <Pill color={mainBlue}>{ticket.handler}</Pill>}
                                <Box sx={{ flex: 1 }} />
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, width: 90 }}>
                                    <Box sx={{ flex: 1 }}><LinearProgress variant='determinate' value={Math.min(avancement, 100)} sx={progressSx(avancement)} /></Box>
                                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1c2340', width: 32, textAlign: 'right' }}>{`${avancement}%`}</Typography>
                                </Box>
                            </Box>
                        </Stack>
                    )}
                </Box>
            </Draggable>
        </ContextMenu>
    )
}

TicketCard.propTypes = {
    ticket: PropTypes.shape({}).isRequired,
    id: PropTypes.number.isRequired,
    activeTicket: PropTypes.object,
    loading: PropTypes.bool,
    onEditAvancement: PropTypes.func,
    showLogin: PropTypes.bool,
}

// Colonne du kanban (style du Suivi des lots) : en-tête blanc avec pastille ronde colorée, titre et compteur,
// tickets sur fond gris clair. Survol d'un ticket glissé depuis une autre colonne : zone de dépôt en pointillés.
const TicketsGroupCard = ({
    title,
    id,
    count,
    accentColor = mainBlue,
    tickets = [],
    activeTicket,
    activeGroup,
    loading,
    description,
    onEditAvancement = () => {},
    showLogin,
    limit,
}) => {
    const isDropTarget = activeTicket && !tickets.some(t => activeTicket?.id === t.id) && id !== ID_TICKETS_TO_COME
    return (
        // overflow visible : un ticket glissé ne doit pas être coupé au bord de sa colonne.
        <Card sx={{ overflow: 'visible' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: '16px 16px', borderBottom: '1px solid #e6eaf2' }}>
                <Circle sx={{ fontSize: 16, color: accentColor }} />
                <Typography sx={{ fontSize: 18, fontWeight: 700, color: '#1c2340' }}>{title}</Typography>
                <Pill>{count}</Pill>
                <Box sx={{ flex: 1 }} />
                <Tooltip title={<span style={{ whiteSpace: 'pre-wrap' }}>{description}</span>}>
                    <Icon sx={{ fontSize: 20, color: '#98a3b8', cursor: 'help' }}>help_outline</Icon>
                </Tooltip>
            </Box>
            <Droppable id={id}>
                <Box sx={{ minHeight: '75vh', position: 'relative', backgroundColor: '#f5f7fa', p: 1 }}>
                    {activeGroup === id && loading ? (
                        <Stack alignItems='center' justifyContent='center' spacing={1} sx={{ position: 'absolute', inset: 0, color: '#6b7893', fontSize: 13 }}>
                            <CircularProgress size={28} />
                            <span>Actualisation…</span>
                        </Stack>
                    ) : (
                        <>
                            {isDropTarget && (
                                <Stack
                                    alignItems='center'
                                    justifyContent='center'
                                    spacing={1}
                                    // Par-dessus les tickets (sinon leurs barres d'avancement le traversent), semi-opaque et
                                    // transparent aux événements souris pour ne pas gêner le dépôt.
                                    sx={{
                                        position: 'absolute',
                                        inset: 8,
                                        zIndex: 2,
                                        pointerEvents: 'none',
                                        border: `2px dashed ${accentColor}`,
                                        borderRadius: '6px',
                                        backgroundColor: `${tintColor(accentColor, 0.12)}e6`,
                                        color: '#33405c',
                                        fontSize: 13,
                                        fontWeight: 600,
                                    }}
                                >
                                    <AddBoxOutlined sx={{ color: accentColor }} />
                                    <span>Déposer pour changer le statut</span>
                                </Stack>
                            )}
                            <Stack spacing={1}>
                                {slice(orderBy(
                                    tickets.map(t => ({ ...t, sortValue: getIconPriority(t.priority)?.sortValue || 999 })),
                                    showLogin ? ['handler', 'sortValue', 'lot'] : ['sortValue', 'lot'], ['asc'],
                                ), 0, limit).map(t => (
                                    <TicketCard
                                        key={t.id}
                                        ticket={t}
                                        id={id}
                                        activeTicket={activeTicket}
                                        loading={loading}
                                        onEditAvancement={onEditAvancement}
                                        showLogin={showLogin}
                                    />
                                ))}
                            </Stack>
                        </>
                    )}
                </Box>
            </Droppable>
        </Card>
    )
}

TicketsGroupCard.propTypes = {
    title: PropTypes.string.isRequired,
    count: PropTypes.string,
    accentColor: PropTypes.string,
    description: PropTypes.string.isRequired,
    tickets: PropTypes.arrayOf(PropTypes.shape({})),
    activeTicket: PropTypes.object,
    activeGroup: PropTypes.number,
    loading: PropTypes.bool,
    id: PropTypes.number.isRequired,
    onEditAvancement: PropTypes.func,
    showLogin: PropTypes.bool,
    limit: PropTypes.number,
}

const MyTicketsApp = () => {
    const userLoginMantis = useSelector(store => store.app.user?.loginMantis, shallowEqual)
    const { login } = useParams()
    const [openDialogAddNote, setOpenDialogAddNote] = useState(false)
    const [editAvancementTicket, setEditAvancementTicket] = useState()
    const [limit, setLimit] = useState(50)

    const isAll = useMemo(() => login === 'all', [login])

    const {
        data: tickets = [],
        reload: reloadTickets,
        status: state,
    } = useFetch(ApplicationConf.support.statisticsCreated(), { method: 'POST', body: isAll ? {} : { handler: login } })

    // Lots DEV Aquaparc (gdl) : le lot affiché et le classement A venir / A faire viennent de ces lots, plus du champ Mantis.
    const { data: devLots } = useFetch(ApplicationConf.devLot.all())

    const [loading, setLoading] = useState(false)

    const [activeElement, setActiveElement] = useState({ idTicket: undefined, idGroup: undefined })
    const [targetGroup, setTargetGroup] = useState()
    const activeTicket = useMemo(() => tickets?.find(s => s.id === activeElement?.idTicket), [activeElement, tickets])

    const dispatch = useDispatch()

    useEffect(() => {
        dispatch(fetchUsers())
    }, [dispatch])

    useTitles(() => [
        { label: 'Mes tickets', href: `tickets/${userLoginMantis}` },
        { label: login, href: `tickets/${login}` },
    ], [login])

    useActions(() => [{
        type: REFRESH,
        loading: state === STATUS.LOADING,
        onClick: () => reloadTickets(),
    }, {
        type: OTHER,
        icon: 'more',
        tooltip: `Afficher 50 de plus (${limit})`,
        onClick: () => setLimit(prev => prev + 50),
    }], [limit, login, state])

    const { ticketsToCome, ticketsToDo, ticketsOnGoing, ticketsDone } = useMemo(() => {
        const ticketsWithLots = withDevLots(tickets ?? [], devLots ?? [])
        const myTickets = isAll ? ticketsWithLots : ticketsWithLots.filter(t => t.handler === login)
        const isLaterLot = (t) => [LOT_GROUP.TO_COME, LOT_GROUP.TO_ARBITRATE].includes(t.lotGroup)
        return {
            ticketsToCome: myTickets.filter(t => [STATUS_TICKETS_LABEL.AFFECTE, STATUS_TICKETS_LABEL.REOUVERT,
                STATUS_TICKETS_LABEL.COMMENTAIRE, STATUS_TICKETS_LABEL.NOUVEAU].includes(t.status) &&
                (isLaterLot(t) || t.dueDate * 1000 >= moment().add(3, 'M').valueOf()),
            ),
            ticketsToDo: myTickets.filter(t => [STATUS_TICKETS_LABEL.AFFECTE, STATUS_TICKETS_LABEL.REOUVERT,
                STATUS_TICKETS_LABEL.COMMENTAIRE, STATUS_TICKETS_LABEL.NOUVEAU].includes(t.status) &&
                (!isLaterLot(t) && t.dueDate * 1000 <= moment().add(3, 'M').valueOf()),
            ),
            ticketsOnGoing: myTickets.filter(t => [STATUS_TICKETS_LABEL.EN_COURS].includes(t.status)),
            ticketsDone: myTickets.filter(t => [STATUS_TICKETS_LABEL.ATTENTE_VALIDATION_INTERNE,
                STATUS_TICKETS_LABEL.EN_VALIDATION_INTERNE,
                STATUS_TICKETS_LABEL.ATTENTE_MISE_EN_PRODUCTION].includes(t.status)),
        }
    }, [isAll, tickets, devLots, login])

    // Compteur de colonne : « affichés / total » quand la limite d'affichage tronque la liste.
    const countLabel = (nb) => (limit < nb ? `${limit} / ${nb}` : String(nb))

    const generalProps = {
        activeTicket,
        activeGroup: targetGroup,
        loading,
        onEditAvancement: setEditAvancementTicket,
        showLogin: isAll,
        limit,
    }

    return (
        <Grid container spacing={2} alignItems='flex-start'>
            <DndContext
                onDragEnd={({ over }) => {
                    if (over?.id && over?.id === activeElement?.idGroup) {
                        setActiveElement()
                    } else if (over?.id === ID_TICKETS_TO_DO) {
                        setTargetGroup(over?.id)
                        setLoading(true)
                        IssuesAction.updateIssues([{ id: activeElement?.idTicket, status: STATUS_TICKETS_LABEL.AFFECTE }]).then(() => {
                            reloadTickets().then(() => {
                                setTargetGroup()
                                setActiveElement()
                                setLoading(false)
                            })
                        })
                    } else if (over?.id === ID_TICKETS_ONGOING) {
                        setTargetGroup(over?.id)
                        setLoading(true)
                        IssuesAction.updateIssues([{ id: activeElement?.idTicket, status: STATUS_TICKETS_LABEL.EN_COURS }]).then(() => {
                            reloadTickets().then(() => {
                                setActiveElement()
                                setTargetGroup()
                                setLoading(false)
                            })
                        })
                    } else if (over?.id === ID_TICKETS_DONE) {
                        setTargetGroup(over?.id)
                        setOpenDialogAddNote(true)
                    } else {
                        setActiveElement()
                    }
                }}
                onDragStart={({ active: { id } }) => {
                    const idTicket = Number(id.split('_')[0])
                    const idGroup = Number(id.split('_')[1])
                    setActiveElement({ idTicket, idGroup })
                }}
                onDragCancel={setActiveElement}
            >
                <Grid size={3}>
                    <TicketsGroupCard
                        id={ID_TICKETS_TO_COME}
                        title='A venir'
                        count={countLabel(ticketsToCome.length)}
                        accentColor={PASTEL_COLOR.grey}
                        description={'Tickets de lots à venir,\nde lots d\'arbitrage\nou avec une date d\'échance >= 3 mois'}
                        tickets={ticketsToCome}
                        {...generalProps}
                    />
                </Grid>
                <Grid size={3}>
                    <TicketsGroupCard
                        id={ID_TICKETS_TO_DO}
                        title='A faire'
                        count={countLabel(ticketsToDo.length)}
                        accentColor={PASTEL_COLOR.orange}
                        description={'Tickets hors lots à venir / d\'arbitrage\navec une date d\'échance <= 3 mois'}
                        tickets={ticketsToDo}
                        {...generalProps}
                    />
                </Grid>
                <Grid size={3}>
                    <TicketsGroupCard
                        id={ID_TICKETS_ONGOING}
                        title='En cours'
                        count={countLabel(ticketsOnGoing.length)}
                        accentColor={mainBlue}
                        description={'Tickets avec le statut "En cours"'}
                        tickets={ticketsOnGoing}
                        {...generalProps}
                    />
                </Grid>
                <Grid size={3}>
                    <TicketsGroupCard
                        id={ID_TICKETS_DONE}
                        title='Fait'
                        count={countLabel(ticketsDone.length)}
                        accentColor={PASTEL_COLOR.green}
                        description={'Tickets avec le statut\n"Attente validation interne",\n"En validation interne"\nou "Attente mise en production'}
                        tickets={ticketsDone}
                        {...generalProps}
                    />
                </Grid>
            </DndContext>
            {editAvancementTicket && (
                <DialogEditAvancement
                    open={!!editAvancementTicket}
                    ticket={editAvancementTicket}
                    onClose={() => setEditAvancementTicket()}
                    onValidate={(avancement) => {
                        setLoading(true)
                        IssuesAction.updateIssues([{ id: editAvancementTicket.id, avancement }]).then(() => {
                            reloadTickets().then(() => setLoading(false))
                        })
                    }}
                />
            )}
            {openDialogAddNote && (
                <DialogAddNoteTicket
                    title={`Ajouter une note pour valider le ticket ${activeTicket?.id}`}
                    open={openDialogAddNote}
                    showTimeSpent
                    timeSpent={activeTicket?.tempspasse ? Number(activeTicket?.tempspasse) : undefined}
                    onClose={() => {
                        setActiveElement()
                        setOpenDialogAddNote(false)
                    }}
                    onValidate={(message, files, time) => {
                        setOpenDialogAddNote(false)
                        setLoading(true)
                        IssuesAction.addNote(activeTicket?.id, `Réalisé par @${login}\n${message}`, files)
                        IssuesAction.updateIssues([{ id: activeTicket?.id, status: STATUS_TICKETS_LABEL.ATTENTE_VALIDATION_INTERNE, avancement: 100, time }]).then(() => {
                            reloadTickets().then(() => {
                                setTargetGroup()
                                setActiveElement()
                                setLoading(false)
                            })
                        })
                    }}
                />
            )}
        </Grid>
    )
}

export default MyTicketsApp